import React, { useRef, useState } from 'react';
import { Folder, FileText, Edit3, Image as ImageIcon, BarChart2, MonitorPlay, Upload } from 'lucide-react';

const categoryIcons = {
  'Documents': FileText,
  'PDF Editor': Edit3,
  'Images': ImageIcon,
  'Data': BarChart2,
  'Presentations': MonitorPlay
};

const styles = {
  wrap: {
    marginBottom: '16px',
    animation: 'fadeUp 0.4s 0.15s ease both',
  },
  zone: (dragging, hasFile, disabled) => ({
    background: dragging ? 'rgba(22, 163, 74, 0.08)' : 'var(--surface)',
    border: dragging
      ? '1px dashed var(--accent)'
      : hasFile
      ? '1px solid var(--border-md)'
      : '1px dashed var(--border-hi)',
    borderRadius: 'var(--radius-md)',
    padding: hasFile ? '18px 22px' : '42px 26px',
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'border-color 0.15s, background 0.15s, padding 0.25s, box-shadow 0.15s',
    opacity: disabled ? 0.55 : 1,
    boxShadow: disabled ? 'none' : '0 18px 40px rgba(15, 23, 42, 0.08)',
  }),
  iconWrap: {
    width: '56px',
    height: '56px',
    border: '1px solid var(--border-hi)',
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 18px',
    fontSize: '24px',
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    transition: 'border-color 0.15s, transform 0.15s',
  },
  heading: {
    fontFamily: 'var(--font-display)',
    fontSize: '18px',
    fontWeight: 500,
    color: 'var(--text)',
    marginBottom: '8px',
  },
  sub: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.7,
  },
  browse: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
    borderRadius: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 500,
    background: 'transparent',
    fontSize: '13px',
    margin: '0 6px',
    transition: 'background 0.2s',
    transform: 'translateY(3px)',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  fileIconBox: {
    width: '46px',
    height: '46px',
    borderRadius: '16px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },
  fileMeta: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  fileName: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileSize: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '2px',
  },
  removeBtn: {
    fontSize: '11px',
    color: 'var(--danger)',
    cursor: 'pointer',
    padding: '7px 12px',
    border: '1px solid rgba(248, 113, 113, 0.5)',
    borderRadius: 'var(--radius)',
    background: 'rgba(248, 113, 113, 0.12)',
    transition: 'background 0.15s, color 0.15s',
    fontFamily: 'var(--font-mono)',
  },
  noSelection: {
    textAlign: 'center',
    padding: '20px',
    fontSize: '12px',
    color: 'var(--text-muted)',
    letterSpacing: '0.04em',
  },
  noSelectionIcon: {
    fontSize: '24px',
    marginBottom: '8px',
  },
};

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function DropZone({ conversion, file, onFile, onRemove, isResetTrigger }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const disabled = !conversion;

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files;
    if (f.length > 0) {
      if (conversion?.id === 'pdf-merge' || conversion.id === 'pdf-organize') {
        onFile(Array.from(f));
      } else {
        onFile(f[0]);
      }
    }
  }

  function handleSelect(e) {
    const f = e.target.files;
    if (f.length > 0) {
      if (conversion?.id === 'pdf-merge' || conversion.id === 'pdf-organize') {
        onFile(Array.from(f));
      } else {
        onFile(f[0]);
      }
    }
    e.target.value = '';
  }

  function handleClick() {
    if (!disabled) inputRef.current?.click();
  }

  if (!conversion) {
    return (
      <div style={styles.wrap}>
        <div style={{ ...styles.zone(false, false, true) }}>
          <div style={styles.noSelectionIcon}>↑</div>
          <div style={styles.noSelection}>
            Select a conversion type above to get started
          </div>
        </div>
      </div>
    );
  }

  if (isResetTrigger) {
    return (
      <div style={styles.wrap}>
        <input
          ref={inputRef}
          type="file"
          multiple={conversion.id === 'pdf-merge' || conversion.id === 'pdf-organize'}
          accept={conversion.accept}
          style={{ display: 'none' }}
          onChange={handleSelect}
        />
        <div
          style={{
            ...styles.zone(dragging, false, false),
            padding: '32px',
            background: dragging ? 'rgba(22, 163, 74, 0.05)' : 'transparent',
            boxShadow: 'none',
          }}
          onClick={handleClick}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <div style={{ color: '#166534', fontWeight: 700, fontSize: '15px', marginBottom: '8px', fontFamily: 'var(--font-display)' }}>
            Convert another file
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
            Drag & drop your {conversion.from} here or click to browse
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <input
        ref={inputRef}
        type="file"
        multiple={conversion.id === 'pdf-merge' || conversion.id === 'pdf-organize'}
        accept={conversion.accept}
        style={{ display: 'none' }}
        onChange={handleSelect}
      />
      <div
        style={styles.zone(dragging, !!file, disabled)}
        onClick={handleClick}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {file ? (
          <div style={styles.fileRow}>
            <div style={styles.fileMeta}>
              <div style={styles.fileName}>{file.name}</div>
              <div style={styles.fileSize}>{formatSize(file.size)}</div>
            </div>
            <button
              style={styles.removeBtn}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
            >
              Remove
            </button>
          </div>
        ) : (
          <>
            <div style={styles.heading}>
              Drop your {conversion.from} file here
            </div>
            <div style={styles.sub}>
              or{' '}
              <button
                style={styles.browse}
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                Choose file
              </button>
              {' '}— accepts {conversion.fromExt} up to 50MB
            </div>
          </>
        )}
      </div>
    </div>
  );
}
